// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import IconAlert from "@stoneage/XDL/Icons/IconAlert";
import IconWarning from "@stoneage/XDL/Icons/IconWarning";
import { xParserOptions } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";
import { useState } from "react";

interface Props {
    label: string;
    tooltipText?: string;
    value: string | number | boolean;
    currentUnit?: string;
    message?: { message: string; type: string };
    extendedStyles?: JettingAppResultStyles;
    shouldLabelTakeHalfWidth?: boolean;
}

export interface JettingAppResultStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
    container?: InjectableCss;
    message?: TypographyPresentationProps;
    messageWrapper?: InjectableCss;
}

export const jettingAppResultStyles: JettingAppResultStyles = {};

const JettingAppResult = ({
    extendedStyles,
    label,
    value,
    tooltipText,
    currentUnit,
    message,
    shouldLabelTakeHalfWidth,
}: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppResultStyles, extendedStyles));
    let bgColor = "transparent";
    let borderColor = "transparent";
    if (message && message.message) {
        bgColor = message.type === "Warning" ? "#FDF9DE" : message.type === "Error" ? "#FBDBE5" : "transparent";
        borderColor = message.type === "Warning" ? "#AC8613" : message.type === "Error" ? "#E4104E" : "transparent";
    }
    return (
        <StyledWrapper {...styles.container} {...{ bgColor, hasMessage: !!(message && message.message), borderColor }}>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.label} {...{ shouldLabelTakeHalfWidth }}>
                    {label}
                    {tooltipText && (
                        <Tooltip {...styles.tooltip} passAsChildren={true}>
                            <Typography>{parse(tooltipText, xParserOptions)}</Typography>
                        </Tooltip>
                    )}
                </Typography>
                <Typography translate="no" {...styles.value}>{`${value} ${currentUnit || ""}`}</Typography>
            </StyledWrapper>
            {message && message.message && (
                <StyledWrapper {...styles.messageWrapper}>
                    <Icon
                        src={message.type === "Error" ? IconAlert : IconWarning}
                        color={message.type === "Error" ? "danger.main" : "warning.main"}
                        size={16}
                    />
                    <Typography {...styles.message}>{message.message}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
export default JettingAppResult;
